import { saveAs } from 'file-saver';
import { AxiosResponse } from 'axios';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import agent from '../api/agents';
import { FilterValues } from '../models/common/filterValues';
import { OffsetRequest } from '../models/offsetRequest';
import { store } from './store';

const errorMessage = (error: any) => {
    const response = error?.response?.data;
    return typeof response === 'string' ? response : 'Unable to complete the request. Please try again.';
};

export default class OffsetRequestStore {
    offsetRequests: OffsetRequest[] = [];
    total = 0;
    isLoading = false;
    isDeciding = false;
    error = '';
    decisionError = '';
    private listVersion = 0;
    private filters: FilterValues = { pageSize: 20, pageNumber: 1, searchKeyword: '' };
    private observedSession: string | null = null;
    private sessionGeneration = 0;
    private isObservingSession = false;

    constructor() {
        makeAutoObservable(this);
    }

    private get sessionIdentity() {
        const user = store.userStore.user;
        return store.authStore.token && user ? `${user.id}:${user.clientId ?? ''}` : null;
    }

    get sessionVersion() {
        return this.observedSession !== null && this.observedSession === this.sessionIdentity
            ? this.sessionGeneration : null;
    }

    private resetSession = (identity: string | null) => {
        this.observedSession = identity;
        this.sessionGeneration++;
        this.listVersion++;
        this.offsetRequests = [];
        this.total = 0;
        this.filters = { pageSize: 20, pageNumber: 1, searchKeyword: '' };
        this.error = '';
        this.decisionError = '';
        this.isLoading = false;
        this.isDeciding = false;
    };

    observeSession = () => {
        if (this.isObservingSession) return;
        this.isObservingSession = true;
        // The registry constructs this store before auth/user stores. Bind only after initialization.
        this.resetSession(this.sessionIdentity);
        reaction(() => this.sessionIdentity, this.resetSession);
    };

    private isCurrentSession = (version: number | null) => version !== null && this.sessionVersion === version;

    private requestContext = (session: number) => ({
        isCurrent: () => this.isCurrentSession(session),
        onError: (response: AxiosResponse) => {
            if (!this.isCurrentSession(session)) return;
            const { status, data, headers } = response;
            const message = typeof data === 'string' ? data : '';
            if (status === 401 && headers['www-authenticate']?.startsWith('Bearer error="invalid_token"')) {
                store.modalStore.closeModal();
                store.authStore.logout();
                toast.error('Session expired. Please login again', { toastId: 'session-expired' });
            } else if (status === 400) {
                toast.error(message || 'Unable to complete the request. Please try again.');
            } else if (status === 401) {
                toast.error(message || 'Unauthorized.');
            } else if (status === 403) {
                toast.error(message || 'Unable to login. Please contact your system administrator.');
            } else if (status === 404) {
                toast.error(message || 'Not Found');
            } else if (status === 409) {
                toast.error(message || 'Conflict');
            }
        }
    });

    filterOffsetRequests = async (filters: FilterValues) => {
        this.observeSession();
        const session = this.sessionVersion;
        if (session === null) return;
        this.filters = filters;
        const version = ++this.listVersion;
        this.isLoading = true;
        this.error = '';
        try {
            const result = await agent.OffsetRequests.filter(filters, this.requestContext(session));
            runInAction(() => {
                if (!this.isCurrentSession(session) || version !== this.listVersion) return;
                this.offsetRequests = result.data;
                this.total = result.total;
            });
        } catch (error) {
            runInAction(() => {
                if (!this.isCurrentSession(session) || version !== this.listVersion) return;
                this.offsetRequests = [];
                this.total = 0;
                this.error = errorMessage(error);
            });
        } finally {
            runInAction(() => {
                if (this.isCurrentSession(session) && version === this.listVersion) this.isLoading = false;
            });
        }
    };

    approveOffsetRequest = (id: string) => this.decide(id);

    rejectOffsetRequest = (id: string, reason: string) => this.decide(id, reason);

    private decide = async (id: string, reason?: string) => {
        this.observeSession();
        const session = this.sessionVersion;
        if (session === null || this.isDeciding) return false;
        this.decisionError = '';
        const rejectReason = reason?.trim();
        if (reason !== undefined && (!rejectReason || reason.length > 2000)) {
            this.decisionError = 'A rejection reason of up to 2000 characters is required.';
            return false;
        }
        this.isDeciding = true;
        try {
            if (reason === undefined) {
                await agent.OffsetRequests.approve(id, store.userStore.displayName, this.requestContext(session));
            } else {
                await agent.OffsetRequests.reject(id, rejectReason!, store.userStore.displayName, this.requestContext(session));
            }
            if (!this.isCurrentSession(session)) return false;
            toast.success(reason === undefined ? 'Offset Credit Request approved.' : 'Offset Credit Request rejected.');
            await this.filterOffsetRequests(this.filters);
            return this.isCurrentSession(session);
        } catch (error: any) {
            if (!this.isCurrentSession(session)) return false;
            runInAction(() => { this.decisionError = errorMessage(error); });
            if (error?.response?.status === 404 || error?.response?.status === 409) {
                await this.filterOffsetRequests(this.filters);
            }
            return false;
        } finally {
            runInAction(() => {
                if (this.isCurrentSession(session)) this.isDeciding = false;
            });
        }
    };

    downloadAttachment = async (id: string, name: string) => {
        this.observeSession();
        const session = this.sessionVersion;
        if (session === null) return;
        try {
            const file = await agent.OffsetRequests.downloadFile(id, this.requestContext(session));
            if (!this.isCurrentSession(session)) return;
            saveAs(file, name);
        } catch (error) {
            if (!this.isCurrentSession(session)) return;
            toast.error(errorMessage(error));
        }
    };
}
